import { useMemo } from "react";

import Typography from "ds/components/Typography";
import Box from "ds/components/Box";
import Counter from "ds/components/Counter";
import { SpacesContext } from "views/Account/SpacesProvider";
import useTypedContext from "hooks/useTypedContext";

import AssignRoleFormListItem from "./ListItem";
import AssignRoleFormFormAdd from "./Add";
import {
  AssignRoleFormEditFields,
  AssignRoleFormAddFields,
  AssignedRole,
  CustomRoleAction,
} from "./types";
import styles from "./styles.module.css";

type AssignRoleFormProps = {
  onCreate: (formData: AssignRoleFormAddFields) => void;
  onEdit?: (formData: AssignRoleFormEditFields, roleAssignmentId: string) => void;
  onDelete: (roleAssignmentId: string, roleName: string) => void;
  setDirtyFields: (formId: string, value: boolean) => void;
  availableRoles: Array<{ id: string; name: string }>;
  assignedRoles: AssignedRole[];
  loading: boolean;
  createLoading?: boolean;
  selectableRoleIds?: Array<string>;
  selectableSpaceIds?: Array<string>;
  customActions?: CustomRoleAction[];
};

const AssignRoleForm = ({
  onCreate,
  onEdit,
  onDelete,
  setDirtyFields,
  availableRoles,
  assignedRoles,
  loading,
  createLoading,
  selectableRoleIds,
  selectableSpaceIds,
  customActions,
}: AssignRoleFormProps) => {
  const { spaces, manageableSpaces } = useTypedContext(SpacesContext);

  const assignedRolesCount = assignedRoles.length;

  const allRolesById = useMemo(
    () =>
      availableRoles?.reduce(
        (prev, next) => ({ ...prev, [next.id]: next.name }),
        {} as Record<string, string>
      ) || {},
    [availableRoles]
  );

  const spaceNamesById = useMemo(
    () =>
      spaces?.reduce(
        (prev, next) => ({ ...prev, [next.id]: next.name }),
        {} as Record<string, string>
      ) || {},
    [spaces]
  );

  const selectableRolesForAdd = useMemo(() => {
    return selectableRoleIds
      ? availableRoles.filter((role) => selectableRoleIds.includes(role.id))
      : availableRoles;
  }, [availableRoles, selectableRoleIds]);

  const selectableSpacesForAdd = useMemo(() => {
    return selectableSpaceIds
      ? manageableSpaces.filter((space) => selectableSpaceIds.includes(space.id))
      : manageableSpaces;
  }, [manageableSpaces, selectableSpaceIds]);

  return (
    <>
      <Box direction="column" gap="large">
        <AssignRoleFormFormAdd
          createLoading={createLoading}
          isLoading={loading}
          setIsDirty={setDirtyFields}
          selectableRoles={selectableRolesForAdd}
          selectableSpaces={selectableSpacesForAdd}
          onCreate={onCreate}
        />

        <Box direction="column" gap="x-large">
          <Box gap="small">
            <Typography tag="h5" variant="p-t5">
              Roles assigned
            </Typography>

            <Counter count={assignedRolesCount} size="small" />
          </Box>

          <div className={styles.divider} />

          {assignedRolesCount === 0 && (
            <Box justify="center" padding="x-large 0">
              <Typography tag="p" variant="p-body3" color="primary">
                No roles assigned yet.
              </Typography>
            </Box>
          )}
        </Box>

        {assignedRoles.map((roleAssignment) => (
          <AssignRoleFormListItem
            setIsDirty={setDirtyFields}
            roleAssignmentId={roleAssignment.id}
            key={roleAssignment.id}
            roleId={roleAssignment.roleID}
            roleName={allRolesById[roleAssignment.roleID]}
            spaceId={roleAssignment.spaceID}
            spaceName={roleAssignment.spaceName || spaceNamesById[roleAssignment.spaceID] || ""}
            hasAccessToSpace={!!spaceNamesById[roleAssignment.spaceID]}
            availableRoles={availableRoles}
            selectableRoleIds={selectableRoleIds}
            selectableSpaceIds={selectableSpaceIds}
            onEdit={onEdit}
            onDelete={onDelete}
            customActions={customActions}
          />
        ))}
      </Box>
    </>
  );
};

export default AssignRoleForm;
